'use client';

import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle } from 'lucide-react'; // Only CheckCircle needed for features list
import { packageTiers, type PackageTier } from '@/lib/packages';

interface PackageSuggestionProps {
  suggestedPackageName?: string;
  estimatedHours?: number;
}

export function PackageSuggestion({ suggestedPackageName, estimatedHours }: PackageSuggestionProps) {
  const [selectedPackage, setSelectedPackage] = useState<PackageTier | null>(null);
  const recommendedTier = packageTiers.find(tier => tier.name === suggestedPackageName);

  if (!recommendedTier) {
    console.warn(`Suggested package "${suggestedPackageName}" not found in defined tiers.`);
    return <p className="text-center text-muted-foreground mt-6">Could not find details for the suggested package: {suggestedPackageName}</p>;
  }

  return (
    <div className="flex justify-center mt-6">
      <Card 
        key={recommendedTier.name} 
        className="flex flex-col shadow-lg transition-all duration-300 ease-in-out hover:shadow-xl border-2 border-primary ring-2 ring-primary/50 w-full max-w-md sm:max-w-sm"
      >
        <CardHeader className="items-center text-center">
          {recommendedTier.icon}
          <CardTitle className="text-xl font-headline mt-2">{recommendedTier.name}</CardTitle>
          <CardDescription className="text-2xl font-bold text-primary mt-1">{recommendedTier.price}</CardDescription>
          {estimatedHours !== undefined && estimatedHours > 0 && ( // Ensure estimatedHours is positive
            <p className="text-sm text-accent font-semibold mt-1">(Est. {estimatedHours} hrs by AI)</p>
          )}
          {recommendedTier.renewal && <p className="text-xs text-muted-foreground">{recommendedTier.renewal}</p>}
        </CardHeader>
        <CardContent className="flex flex-col flex-grow">
          <p className="text-sm text-muted-foreground mb-4 text-center min-h-[60px]">{recommendedTier.description}</p>
          <ul className="space-y-2 text-sm mb-6 flex-grow">
            {recommendedTier.features.map((feature, idx) => (
              <li key={idx} className="flex items-start">
                <CheckCircle className="h-4 w-4 mr-2 mt-0.5 text-green-500 flex-shrink-0" />
                <span>{feature}</span>
              </li>
            ))}
          </ul>
          {recommendedTier.discountNote && <p className="text-xs text-accent text-center mb-3 font-semibold">{recommendedTier.discountNote}</p>}
          <Button 
            onClick={recommendedTier.onClick}
            className="mt-auto w-full bg-accent hover:bg-accent/90 text-accent-foreground"
          >
            {recommendedTier.ctaText || 'Learn More'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
